import type {
  Company,
  CompanyGroup,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyListResponse,
  PoolOption,
} from '@/types/companyManagement.types';
import { ApiClient } from '@/config/axios.config';

/**
 * CompanyManagementService — wraps the /companies API endpoints.
 * Auth (Bearer token) is handled automatically by ApiClient.
 */
export class CompanyManagementService {
  static async getCompanies(): Promise<CompanyListResponse> {
    const companies = await ApiClient.get<Company[]>('/companies?includeDetails=true');
    return {
      companies,
      totalCount: companies.length,
      totalPages: 1,
      pageNumber: 1,
      pageSize: companies.length,
    };
  }

  static async getCompanyById(id: number): Promise<Company> {
    return ApiClient.get<Company>(`/companies/${id}`);
  }

  static async createCompany(data: CreateCompanyRequest): Promise<Company> {
    return ApiClient.post<Company>('/companies', data);
  }

  static async updateCompany(id: number, data: UpdateCompanyRequest): Promise<Company> {
    return ApiClient.put<Company>(`/companies/${id}`, data);
  }

  static async deleteCompany(id: number): Promise<void> {
    await ApiClient.delete<void>(`/companies/${id}`);
  }

  static async getPools(): Promise<PoolOption[]> {
    const pools = await ApiClient.get<{ PoolId: number; Name: string }[]>('/reference-data/pools');
    return pools.map((p) => ({ poolId: p.PoolId, pool: p.Name }));
  }

  /**
   * Reuses the same /usergroups endpoint as UserManagementService.
   */
  static async getAvailableGroups(): Promise<CompanyGroup[]> {
    const groups = await ApiClient.get<{ id: number; code: string; name: string }[]>(
      '/usergroups?minimal=true'
    );
    return groups
      .map((g) => ({ code: g.code, name: g.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
}
